import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState, AppDispatch } from "../../../../../store";
import { getCompanies } from "../../../../../features/companies/companiesSlice";
import { createChatbot, updateChatbot } from "../../../../../features/ai_chatbots/aiChatbotsSlice";
import companiesService from "../../../../../features/companies/companiesService";
import { ChatbotFormData, ChatbotSettings, AIChatbotFormProps } from "../types";

export const useAIChatbotForm = ({
  chatbot,
  onSuccess,
  onHide,
  defaultCompanyId,
  defaultModule,
}: Pick<AIChatbotFormProps, "chatbot" | "onSuccess" | "onHide" | "defaultCompanyId" | "defaultModule">) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const { companies } = useSelector((state: RootState) => state.companies);

  const isEditMode = !!chatbot;
  const isMarketingSpecialist = user?.roles?.includes("marketing_specialist");
  const isCompanyOwner = user?.roles?.includes("company_owner");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [companiesOptions, setCompaniesOptions] = useState<any[]>([]);

  const initialSettings: ChatbotSettings = {
    assistant_profile: {
      persona: "",
      tone: "friendly",
      default_language: "en",
      reply_in_user_language: false,
      answer_format: "short_paragraphs",
      max_answer_tokens: 250,
    },
    retrieval: {
      top_k: 5,
      min_score: 0,
      snippet_chars: 500,
      chunk_size: 500,
      chunk_overlap: 60,
    },
    guardrails: {
      banned_topics: [],
      safety_policies: [],
      no_context_behavior: "ask_clarifying",
    },
    limits: {
      max_input_tokens: 1200,
      max_output_tokens: 450,
      rate_per_min: 30,
      cache_ttl_seconds: 3600,
    },
    provider: { name: "openai", model: "gpt-5.2" },
  };

  const [settingsData, setSettingsData] = useState<ChatbotSettings>(initialSettings);

  const [formData, setFormData] = useState<ChatbotFormData>({
    name: chatbot?.name || "",
    description: chatbot?.description || "",
    initial_message: chatbot?.initial_message || t("initial_message_default") || "Hi there! How can I assist you today?",
    // API keys are masked from backend (e.g., "sk-proj-abc...xyz")
    // If masked key exists, show it; otherwise empty
    openai_api_key: chatbot?.openai_api_key || "",
    claude_api_key: chatbot?.claude_api_key || "",
    model: chatbot?.model || "gpt-5.2",
    general_learning_set: chatbot?.general_learning_set || "",
    db_metadata_endpoint: chatbot?.db_metadata_endpoint || "",
    db_query_endpoint: chatbot?.db_query_endpoint || "",
    allowed_domains: chatbot?.allowed_domains?.join("\n") || "",
    company_id: chatbot?.company_id || defaultCompanyId || "",
    widget_settings: typeof chatbot?.widget_settings === "string"
      ? chatbot.widget_settings
      : chatbot?.widget_settings
        ? JSON.stringify(chatbot.widget_settings)
        : "{}",
    status: chatbot?.status || "active",
    is_active: chatbot?.is_active ?? true,
    public_page_enabled: chatbot?.public_page_enabled ?? false,
    settings: chatbot?.settings || undefined,
    training_status: chatbot?.training_status || undefined,
    chat_mode: chatbot?.chat_mode || undefined,
    module: (chatbot as any)?.module || defaultModule || undefined,
    flows: (chatbot as any)?.flows || [],
  });

  // Initialize settings from chatbot if editing
  useEffect(() => {
    if (chatbot) {
      // Update basic form fields
      setFormData((prev) => ({
        ...prev,
        name: chatbot.name || "",
        description: chatbot.description || "",
        initial_message: chatbot.initial_message || prev.initial_message,
        openai_api_key: chatbot.openai_api_key || "",
        claude_api_key: chatbot.claude_api_key || "",
        model: chatbot.model || "gpt-5.2",
        general_learning_set: chatbot.general_learning_set || "",
        db_metadata_endpoint: chatbot.db_metadata_endpoint || "",
        db_query_endpoint: chatbot.db_query_endpoint || "",
        allowed_domains: chatbot.allowed_domains?.join("\n") || "",
        company_id: chatbot.company_id || prev.company_id,
        widget_settings: typeof chatbot.widget_settings === "string"
          ? chatbot.widget_settings
          : chatbot.widget_settings
            ? JSON.stringify(chatbot.widget_settings)
            : prev.widget_settings,
        status: chatbot.status || "active",
        is_active: chatbot.is_active ?? true,
        public_page_enabled: chatbot.public_page_enabled ?? false,
        flows: (chatbot as any)?.flows || [],
      }));

      // Update settings
      if (chatbot.settings) {
        setSettingsData((prev) => ({
          ...prev,
          ...chatbot.settings,
          assistant_profile: { ...prev.assistant_profile, ...chatbot.settings.assistant_profile },
          retrieval: { ...prev.retrieval, ...chatbot.settings.retrieval },
          guardrails: { ...prev.guardrails, ...chatbot.settings.guardrails },
          limits: { ...prev.limits, ...chatbot.settings.limits },
          provider: { ...prev.provider, ...chatbot.settings.provider },
        }));
      }
    }
  }, [chatbot]);

  // Company loading logic
  useEffect(() => {
    if (!isCompanyOwner && companies.length === 0) {
      dispatch(getCompanies({}));
    }
  }, [dispatch, companies.length, isCompanyOwner]);

  useEffect(() => {
    if (isCompanyOwner) {
      companiesService.getMyCompany()
        .then((mc) => {
          if (mc && mc.id) {
            setCompaniesOptions([{ id: mc.id, company_name: mc.company_name, company_type: mc.company_type }]);
            if (!formData.company_id || Number(formData.company_id) !== Number(mc.id)) {
              setFormData((prev) => ({ ...prev, company_id: Number(mc.id) }));
            }
          }
        })
        .catch(() => setCompaniesOptions([]));
    } else {
      const seen = new Set<number>();
      const unique = companies.filter((c: any) => {
        if (seen.has(c.id)) return false;
        seen.add(c.id);
        return true;
      });
      setCompaniesOptions(unique);
    }
  }, [companies, isCompanyOwner]);

  useEffect(() => {
    if (!isEditMode && defaultCompanyId && formData.company_id !== defaultCompanyId) {
      setFormData((prev) => ({ ...prev, company_id: Number(defaultCompanyId) }));
    }
  }, [defaultCompanyId, isEditMode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async () => {
    setValidationErrors([]);

    if (!formData.name.trim()) {
      setValidationErrors((prev) => [...prev, t("name_is_required")]);
      return;
    }
    if (!formData.company_id) {
      setValidationErrors((prev) => [...prev, t("company_is_required")]);
      return;
    }

    setIsSubmitting(true);

    // Prepare submission data
    const submissionData: any = {
      ...formData,
      company_id: Number(formData.company_id),
      allowed_domains: formData.allowed_domains
        .split("\n")
        .map((d) => d.trim())
        .filter((d) => d),
      // Ensure widget_settings is an object
      widget_settings: typeof formData.widget_settings === 'string'
        ? JSON.parse(formData.widget_settings || "{}")
        : formData.widget_settings,
    };

    // Handle API keys: remove empty strings or masked keys (means "keep existing key")
    // Masked keys contain "..." (e.g., "sk-proj-abc...xyz")
    // Only send API keys if user provided a new value (not empty and not masked)
    if (!submissionData.openai_api_key || submissionData.openai_api_key === "" || submissionData.openai_api_key.includes("...")) {
      delete submissionData.openai_api_key;
    }
    if (!submissionData.claude_api_key || submissionData.claude_api_key === "" || submissionData.claude_api_key.includes("...")) {
      delete submissionData.claude_api_key;
    }

    // Also remove keys based on provider (only send relevant key)
    const providerName = settingsData.provider?.name || "openai";
    if (providerName === "openai") {
      delete submissionData.claude_api_key;
    } else if (providerName === "claude") {
      delete submissionData.openai_api_key;
    }

    // Include settings
    if (isEditMode) {
      submissionData.settings = settingsData;
    } else {
      // For create, we might want to include initial settings too if backend supports it on create
      // Assuming backend accepts settings on create as well
      submissionData.settings = settingsData;
    }

    // Sync model from settings.provider.model to formData.model
    // This ensures model column is updated when model is changed in advance settings
    if (settingsData.provider?.model) {
      submissionData.model = settingsData.provider.model;
    }

    try {
      let result;
      if (isEditMode && chatbot) {
        result = await dispatch(updateChatbot({ id: chatbot.id, chatbotData: submissionData })).unwrap();
      } else {
        result = await dispatch(createChatbot(submissionData)).unwrap();
      }
      if (onSuccess) onSuccess(result);
      // We don't call onHide() automatically if we want to proceed to next steps
      // Checking if onSuccess handles it or if we should default to hide
      // But typically current implementation called onHide(). 
      // Let's keep calling onHide unless onSuccess returns something to prevent it?
      // Or better, let the parent decide when to close/navigate.
      // But preserving backward compatibility:
      // If parent is the new Wizard, it won't pass onHide or will pass a dummy execution.
      // Actually, existing modal usage assumes onHide closes modal.
      if (onHide) onHide();
    } catch (error) {
      console.error("Error submitting form:", error);
      setValidationErrors(["An error occurred while saving the chatbot"]);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    setFormData,
    settingsData,
    setSettingsData,
    isSubmitting,
    validationErrors,
    companiesOptions,
    handleInputChange,
    handleSubmit,
    isEditMode,
    isCompanyOwner,
    isMarketingSpecialist,
  };
};

